import React, { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';

/**
 * ImpersonationBanner
 *
 * Rendered on every page when an admin is impersonating another user.
 * Reads impersonation metadata from localStorage (set by AdminDashboard
 * handleImpersonate). Provides a one-click "Return to Admin" that
 * restores the original admin session from sessionStorage.
 */
export default function ImpersonationBanner() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('impersonation_active');
    if (raw) {
      try {
        setInfo(JSON.parse(raw));
      } catch {
        /* corrupt entry — ignore */
      }
    }

    // Also listen for storage changes (e.g. returning to admin in another tab)
    const handleStorage = (e) => {
      if (e.key === 'impersonation_active') {
        if (e.newValue) {
          try {
            setInfo(JSON.parse(e.newValue));
          } catch {
            setInfo(null);
          }
        } else {
          setInfo(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  if (!info) return null;

  const handleReturn = () => {
    const adminToken = sessionStorage.getItem('admin_token_backup');
    const adminUser = sessionStorage.getItem('admin_user_backup');
    if (!adminToken || !adminUser) {
      alert('No admin session backup found. Please log in as admin again.');
      return;
    }
    localStorage.setItem('token', adminToken);
    localStorage.setItem('user', adminUser);
    localStorage.removeItem('impersonation_active');
    sessionStorage.removeItem('admin_token_backup');
    sessionStorage.removeItem('admin_user_backup');
    window.location.href = '/admin?section=users';
  };

  let expiresLabel = '';
  try {
    expiresLabel = new Date(info.expires_at).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    expiresLabel = info.expires_at;
  }

  return (
    <div
      className="sticky top-0 z-[60] bg-amber-500 text-black px-4 py-2 text-sm font-medium flex items-center justify-center gap-3"
      role="alert"
      aria-live="polite"
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span>
        You are impersonating <strong>{info.target_email}</strong>
        {info.impersonated_by ? ` (admin: ${info.impersonated_by})` : ''}.{' '}
        Session expires {expiresLabel}.
      </span>
      <button
        onClick={handleReturn}
        className="ml-2 px-3 py-1 bg-black text-white rounded hover:bg-zinc-800 transition-colors text-xs uppercase tracking-wide shrink-0"
      >
        Return to Admin
      </button>
    </div>
  );
}
